<script lang="ts">
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import type { AnyItem, GlobalSingleRelationFieldId } from '#layers/data-model/public';

export interface DisplayRelatedValuesProps extends DisplayBlockProps<AnyItem | AnyItem[]> {
	fieldId: GlobalSingleRelationFieldId;
}

const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_related_values.name',
	description: '$t:blocks.display_related_values.description',
	icon: 'lucide:link',
	category: 'display',
	hidden: true,
}</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ensureArray, isNullishOrEmpty } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RenderCollectionDisplayTemplate from '#layers/blocks/components/Render/RenderCollectionDisplayTemplate.vue';
import { usePageDisplayMode } from '#layers/blocks/composables/block-context/use-page-display-mode';
import { getRelatedItemDetailRoute } from '#layers/blocks/utils/relation/related-item-detail-route';
import { CollectionReads, getCollectionPrimaryKeyFields, RelationRowReads, resolveRelationPreview, useSingleRelationField, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { useCollectionRoutes } from '#layers/spaces/public';

defineOptions({
	inheritAttrs: false,
});

const { fieldId, root, value } = defineProps<DisplayRelatedValuesProps>();

;

const field = useSingleRelationField(() => fieldId);

const isList = computed(() => field.value?.isList ?? false);
const workspaceSchema = useWorkspaceSchemaStore();
// Rows of an unpreviewable relation may still sit in the cache from other reads; they are not its preview.
const isPreviewUnavailable = computed(() => !!field.value && resolveRelationPreview(field.value, workspaceSchema.querySchema).kind === RelationRowReads.Unavailable);
const { detailRoute } = useCollectionRoutes(() => field.value?.oppositeCollectionId);
const oppositeCollectionPrimaryKeyFields = computed(() => field.value?.oppositeCollection ? getCollectionPrimaryKeyFields(field.value.oppositeCollection) : []);
const pageDisplayMode = usePageDisplayMode();
const itemDetailRoute = computed(() => {
	if (pageDisplayMode.value || !root || !value || Array.isArray(value)) {
		return null;
	}

	const primaryKeyFields = oppositeCollectionPrimaryKeyFields.value;

	try {
		return getRelatedItemDetailRoute(value, primaryKeyFields, detailRoute) ?? null;
	}
	catch {
		return null;
	}
});
</script>

<template>
	<DisplayTemplateConfigurationError v-if="!field" :error="$t('blocks.display_related_values.error_field_misconfigured')" />
	<DTooltip v-else-if="isPreviewUnavailable" :text="$t('blocks.display_related_values.preview_unavailable_tooltip')">
		<span class="truncate text-muted">{{ $t('blocks.display_related_values.preview_unavailable') }}</span>
	</DTooltip>
	<DDisplayValueEmpty v-else-if="!value || (Array.isArray(value) && isNullishOrEmpty(value))" />
	<template v-else-if="value && !isList">
		<DTooltip
			v-if="itemDetailRoute"
			:text="$t('common.go_to_related_item')"
		>
			<DButton
				variant="ghost"
				color="neutral"
				class="
					group relative -mx-2.5 h-lh w-max max-w-stretch min-w-0 truncate py-0 leading-[inherit] font-normal text-default
					[--affordance:2rem]
					focus-visible:ring-inset
				"
				:to="itemDetailRoute"
			>
				<!-- A spacer rather than padding, because padding cannot shrink: a tight cell spends this
					space on the value instead, and the mask hides whatever ends up under the icon. -->
				<span
					class="
						flex max-w-full min-w-0 items-center overflow-hidden
						ltr:group-hover:mask-r-from-[calc(100%-var(--affordance))]
						ltr:group-focus-visible:mask-r-from-[calc(100%-var(--affordance))]
						rtl:group-hover:mask-l-from-[calc(100%-var(--affordance))]
						rtl:group-focus-visible:mask-l-from-[calc(100%-var(--affordance))]
					"
				>
					<!-- Related rows render as the list read returns them: the engine returns an include with its target's list output. -->
					<RenderCollectionDisplayTemplate
						:collection-id="field.oppositeCollectionId"
						:item="value"
						:read="CollectionReads.Many"
						:root="false"
						layout="fit-content"
						class="h-full"
						size="sm"
					/>
					<span class="w-(--affordance) min-w-0 shrink" />
				</span>
				<span
					class="
						pointer-events-none absolute inset-y-0 inset-e-0 flex w-(--affordance) items-center justify-end pe-2.5 opacity-0
						transition-opacity
						group-hover:opacity-100
						group-focus-visible:opacity-100
					"
				>
					<DIcon name="lucide:arrow-up-right" class="size-4" />
				</span>
			</DButton>
		</DTooltip>
		<RenderCollectionDisplayTemplate
			v-else
			:collection-id="field.oppositeCollectionId"
			:item="value"
			:read="CollectionReads.Many"
			:root="false"
			class="h-full"
			size="sm"
		/>
	</template>
	<DisplayRelatedValuesDropdown
		v-else
		:item="item!"
		:related-items="ensureArray(value)"
		:field
	/>
</template>
